<template>
  <div class="product-details-page ws-grid">
    <main-header />

    <div class="sub-navigation u-typography-helvetica u-text u-text--s">
      <router-link
        class="sub-navigation__nav-item nav-link"
        :to="{ name: 'MainPage', params: { hashedDataId, doctorSlug } }"
      >
        <u-icon class="nav-link__icon" name="left" />
        <span class="sub-navigation__nav-item--grey">{{ $t('link.webshop') }}</span>
      </router-link>

      <span class="sub-navigation__nav-item sub-navigation__nav-item--grey">&bull;</span>

      <span class="sub-navigation__nav-item">{{ productNameDisplayValue }}</span>
    </div>

    <section class="gallery-section">
      <product-gallery
        class="gallery-section__gallery ws-col-7-gte1200"
        :product-name="productName"
        :product-size="productSize"
      />

      <div class="gallery-section__description ws-col-8-gte960 ws-col-5-gte1200">
        <div class="gallery-section-description">
          <div class="gallery-section-description__custom">
            <slot name="description"></slot>
          </div>

          <div class="gallery-section-description__common common-description">
            <div class="options">
              <span v-if="isProductAvailable" class="u-typography-helvetica u-text u-text--l">{{
                formatPrice(productPrice)
              }}</span>
              <template v-if="hasMoreThanOneSize">
                <u-toggle-button
                  v-for="option of productSizeOptions"
                  :key="getProductSizeValue(option)"
                  v-model="selectedProductSizeValue"
                  class="options__size-select-button"
                  :value="getProductSizeValue(option)"
                  :disabled="getIsProductOptionDisabled(option)"
                  :label="formatProductSize(option)"
                />
              </template>
              <span v-else class="options__size-option u-typography-helvetica u-text u-text--m">{{
                formatProductSize(product)
              }}</span>
            </div>
            <div
              v-if="isUSA"
              class="common-description__usa-tax-hint u-typography-helvetica u-text u-text--xs"
            >
              All Prices Include Sales Tax
            </div>

            <div v-if="hasPaypalPayLaterOption" class="common-description__paypal-option">
              <pay-pal-pay-later
                placement="payment"
                :payment-amount="productPrice"
                logo-position="top"
              />
            </div>

            <add-to-cart-button
              class="common-description__add-to-cart-button"
              :is-product-in-cart="isProductInCart"
              :disabled="isAddToCartDisabled"
              @click="onAddProductToCart"
            />

            <expandable-list>
              <template v-slot:usage-title>
                {{ $t('discover.expandableList.section.whenToUse.title') }}
              </template>

              <template v-slot:usage-body>
                {{ usageDisplayValue }}
              </template>

              <template v-slot:how-to-title>
                {{ $t('discover.expandableList.section.howToUse.title') }}
              </template>

              <template v-slot:how-to-body>
                <how-to-use-section :product-name="productName" />
              </template>

              <template v-slot:good-title>
                <span>
                  {{ $t('discover.expandableList.section.goodToKnow.title') }}
                </span>
              </template>

              <template v-slot:good-body>
                <good-to-know-section :product-name="productName" />
              </template>
            </expandable-list>
          </div>
        </div>
      </div>
    </section>

    <section class="video-section">
      <video
        v-if="productVideo"
        ref="productVideo"
        class="video-section__video"
        playsinline
        autoplay
        loop
        muted
      >
        <source :src="productVideo" type="video/mp4" />
      </video>
    </section>

    <slot name="custom-sections"></slot>

    <section class="catalog-section">
      <h2 class="catalog-section__title u-typography-helvetica u-text-display u-text-display--s">
        {{ $t('section.yourRoutine.completeYourRoutineLabel') }}
      </h2>

      <products-catalog :product-page-name="productName" />
    </section>

    <footer class="footer-section">
      <div class="footer-section__copyright copyright">
        © {{ $t('checkout.allRightsReserved') }}
      </div>

      <nav class="footer-section__links footer-links">
        <a class="footer-links__link" :href="footerLinks.legal" target="_blank">
          {{ $t('checkout.legal') }}
        </a>

        <span class="footer-links__bullet">&bull;</span>

        <a class="footer-links__link" :href="footerLinks.termsOfUse" target="_blank">
          {{ $t('footer.termsOfUseLabel') }}
        </a>

        <span class="footer-links__bullet">&bull;</span>

        <a class="footer-links__link" :href="footerLinks.termsOfSales" target="_blank">
          {{ $t('footer.termsOfSalesLabel') }}
        </a>

        <span class="footer-links__bullet">&bull;</span>

        <a class="footer-links__link" :href="footerLinks.privacyNoticeDoctors" target="_blank">{{
          $t('checkout.privacyNoticeDoctors')
        }}</a>

        <span class="footer-links__bullet">&bull;</span>

        <a class="footer-links__link" :href="footerLinks.privacyNoticePatients" target="_blank">{{
          $t('checkout.privacyNoticePatients')
        }}</a>
      </nav>
    </footer>

    <cart-modal />
    <do-you-want-to-switch-formula-modal
      ref="switchFormulaWarningPopup"
      :next-formula-name="switchFormulaWarningModalConfig.nextFormulaName"
      :previous-formula-name="switchFormulaWarningModalConfig.previousFormulaName"
    />
    <do-you-want-to-change-volume-modal
      ref="changeVolumeWarningPopup"
      :next-size="changeVolumeWarningConfig.nextSize"
      :previous-size="changeVolumeWarningConfig.previousSize"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { UIcon, UToggleButton } from 'universkin-shared';

import MainHeader from '@/components/common/app-header/MainHeader.vue';
import ProductGallery from '@/components/product-subpages/ProductGallery.vue';
import ExpandableList from '@/components/common/ExpandableList.vue';
import HowToUseSection from '@/components/discover/HowToUseSection.vue';
import GoodToKnowSection from '@/components/discover/GoodToKnowSection.vue';
import AddToCartButton from '@/components/common/AddToCartButton.vue';
import ProductsCatalog from '@/components/product-subpages/ProductsCatalog.vue';
import DoYouWantToSwitchFormulaModal from '@/components/product-subpages/DoYouWantToSwitchFormulaModal.vue';
import DoYouWantToChangeVolumeModal from '@/components/product-subpages/DoYouWantToChangeVolumeModal.vue';

import { productConfigMixin } from '@/mixins/productConfigMixin';
import { priceFormatterMixin } from '@/mixins/priceFormatterMixin';

import {
  getProductPackGenericEyeSerumFormulaId,
  getProductPackName,
  getProductPackPrice,
  getProductPackSize,
  isProductPackAvailable,
  getProductPackItems,
  isProductPackGenericEyeSerum,
  isProductPackInStock,
  getProductPackUsage,
  isProductPackSerum
} from '@/models/core/ProductPack';

import { getPackItemIngredients } from '@/models/core/PackItem';

import {
  getProductOptionsDefaultOption,
  getProductOptionsOptionByFormulaIdOrDefault,
  getProductOptionsProductPacksList
} from '@/models/core/ProductOptions';
import {
  getCartItemFormulaIds,
  getCartItemName,
  getCartItemProductPack,
  getCartItemSize
} from '@/models/core/CartItem';
import { getSizeUnits, getSizeValue } from '@/models/core/values';

import { formatProductSize } from '@/utils/common';
import { log } from '@/utils/loger';

import { types } from '@/store/types';
import {
  EYE_SERUM_GENERIC_FORMULA_LABELS,
  SERUM_USAGES,
  SIZE_MEASUREMENT_UNIT_KEY,
  PRODUCT_NAMES,
  TreatmentDurationConfig
} from '@/constants/treatment';
import { PRODUCTS_CONFIGURATION } from '@/constants/your-routine';
import { COUNTRY_ISO } from '@/constants/countries';
import { getIngredientName } from '@/models/core/Ingredient';

const COUNTRIES_WITH_DMAE_RESTRICTIONS = [
  COUNTRY_ISO.AUSTRALIA.toLowerCase(),
];

const USAGE_CONFIG = {
  [SERUM_USAGES.MORNING_ONLY]: 'discover.usage.am',
  [SERUM_USAGES.EVENING_ONLY]: 'discover.usage.pm',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'discover.usage.amAndPm',
  [SERUM_USAGES.ONE_MORNING_ONE_EVENING]: 'discover.usage.amAndPm'
};

const PRODUCT_USAGE = {
  [PRODUCT_NAMES.FACE_SERUM]: USAGE_CONFIG,
  [PRODUCT_NAMES.EYE_CONTOUR_SERUM]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_O]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_P_LIGHT]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_P_RICH]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_1]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_2]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_3]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_B]: USAGE_CONFIG,
  [PRODUCT_NAMES.NEXULTRA_L]: USAGE_CONFIG,
  [PRODUCT_NAMES.GEL]: USAGE_CONFIG,
  [PRODUCT_NAMES.UNIVERSKIN_H]: USAGE_CONFIG,
  [PRODUCT_NAMES.UNIVERSKIN_M]: USAGE_CONFIG,
  [PRODUCT_NAMES.SUNSCREEN_SPF]: 'discover.usage.every2hour',
  [PRODUCT_NAMES.SUNSCREEN_CRM]: 'discover.usage.every2hour',
  [PRODUCT_NAMES.SUNSCREEN_FLD]: 'discover.usage.every2hour',
  [PRODUCT_NAMES.NEXULTRA_UV]: 'discover.usage.every2hour',
};

export default {
  name: 'ProductDetailsPageTemplate',
  components: {
    MainHeader,
    UIcon,
    UToggleButton,
    ProductGallery,
    ExpandableList,
    HowToUseSection,
    GoodToKnowSection,
    AddToCartButton,
    ProductsCatalog,
    DoYouWantToSwitchFormulaModal,
    DoYouWantToChangeVolumeModal,
    PayPalPayLater: () => import('@/components/shipping/payment-method/PayPalPayLater.vue'),
    CartModal: () => import('@/components/cart/CartModal.vue')
  },
  mixins: [productConfigMixin, priceFormatterMixin],
  props: {
    productName: {
      type: String,
      required: true
    },
    doctorSlug: {
      type: String,
      required: true
    },
    hashedDataId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      productVideo: null,
      switchFormulaWarningModalConfig: {},
      changeVolumeWarningConfig: {},
      selectedProductOption: null
    };
  },
  computed: {
    ...mapState({
      selectedGenericFormulaId: types.state.SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS,
      footerLinks: types.getters.FOOTER_LINKS,
      getIsProductPackAddedToCart: types.getters.GET_IS_PRODUCT_PACK_ADDED_TO_CART,
      getIsProductNameInCart: types.getters.GET_IS_PRODUCT_ADDED_TO_CART_BY_NAME,
      getIsProductRecommended: types.getters.GET_IS_PRODUCT_RECOMMENDED,
      hasPaypalPayLaterOption: types.getters.HAS_PAYPAL_PAY_LATER_OPTION,
      doesTreatmentContainEyeSerumGeneric: types.getters.DOES_TREATMENT_CONTAIN_EYE_SERUM_GENERIC,
      getProductFromCart: types.getters.GET_PRODUCT_FROM_CART,
      eyeSerumGenericFormulaIds: types.getters.EYE_SERUM_GENERIC_FORMULA_IDS,
      cartItems: types.getters.CART_ITEMS,
      deliveryCountry: types.getters.DELIVERY_COUNTRY
    }),
    productSizeOptions() {
      const productOptions = this.allProductOptions[this.productName];
      const defaultOption = getProductOptionsDefaultOption(productOptions);

      if (isProductPackGenericEyeSerum(defaultOption)) {
        // all options of generic eye serum have the same size,
        // so we return only one of them
        const option = getProductOptionsOptionByFormulaIdOrDefault(
          this.selectedGenericFormulaId,
          productOptions
        );

        return [option];
      }

      return getProductOptionsProductPacksList(productOptions);
    },
    hasMoreThanOneSize() {
      return this.productSizeOptions.length > 1;
    },
    product: {
      get() {
        if (this.isEyeSerumGeneric) {
          const options = getProductOptionsProductPacksList(
            this.allProductOptions[this.productName]
          );
          const defaultOption = getProductOptionsDefaultOption(
            this.allProductOptions[this.productName]
          );

          return this.selectedGenericFormulaId
            ? options.find(
                option =>
                  getProductPackGenericEyeSerumFormulaId(option) === this.selectedGenericFormulaId
              )
            : defaultOption;
        }

        return this.selectedProductOption;
      },
      set(product) {
        this.setSelectedProductOption(product);
      }
    },
    isProductInCart() {
      return this.getIsProductPackAddedToCart(this.product);
    },
    productNameDisplayValue() {
      const { title, titleRecommended } = PRODUCTS_CONFIGURATION[this.productName];

      return this.isRecommended && titleRecommended ? this.$t(titleRecommended) : this.$t(title);
    },
    productSize() {
      return getProductPackSize(this.product);
    },
    isRecommended() {
      return this.getIsProductRecommended(this.productName);
    },
    productPrice() {
      return getProductPackPrice(this.product);
    },
    isProductAvailable() {
      return isProductPackAvailable(this.product);
    },
    isProductInStock() {
      return isProductPackInStock(this.product);
    },
    firstBottle() {
      const [firstBottle] = getProductPackItems(this.selectedProductOption);

      return getPackItemIngredients(firstBottle);
    },
    secondBottle() {
      const [, secondBottle] = getProductPackItems(this.selectedProductOption);

      if (secondBottle) {
        return getPackItemIngredients(secondBottle);
      }

      return [];
    },
    ingredientsBoxStyles() {
      return [...this.firstBottle, ...this.secondBottle].map(ingredient => {
        const name = getIngredientName(ingredient);

        return {
          backgroundColor: `var(--u-color-${name})`
        };
      });
    },
    isAddToCartDisabled() {
      let disabledByDMAE = false;
      let disabledByGenerixFormulas = false;
      const isFormulaGenerix = isProductPackGenericEyeSerum(this.selectedProductOption);
      // Business Rule : check if DMAE is an ingredient
      
      if(COUNTRIES_WITH_DMAE_RESTRICTIONS.includes(this.deliveryCountry) && (isProductPackSerum(this.selectedProductOption)) && !isFormulaGenerix)  
      {
           [...this.firstBottle, ...this.secondBottle].forEach(ingredient => {
           const name = getIngredientName(ingredient);
           if(name==='dmae') disabledByDMAE=true;
          });
       }
       if(COUNTRIES_WITH_DMAE_RESTRICTIONS.includes(this.deliveryCountry) && isFormulaGenerix && ((this.selectedGenericFormulaId.startsWith('WRINKLES_2')||this.selectedGenericFormulaId.startsWith('WRINKLES_3')))) 
       {
            disabledByGenerixFormulas = true;
       }
       return disabledByDMAE || !this.isProductInStock || !this.isProductAvailable || disabledByGenerixFormulas;
    },
    isEyeSerumGeneric() {
      return isProductPackGenericEyeSerum(
        getProductOptionsDefaultOption(this.allProductOptions[this.productName])
      );
    },
    isOtherProductOptionInCart() {
      const isNameInCart = this.getIsProductNameInCart(this.productName);
      const isSameOptionInCart = this.getIsProductPackAddedToCart(this.product);

      return isNameInCart && !isSameOptionInCart;
    },
    isSerum() {
      return isProductPackSerum(this.product);
    },
    isEyeContourGeneric() {
      return isProductPackGenericEyeSerum(this.product);
    },
    selectedProductSizeValue: {
      get() {
        return this.getProductSizeValue(this.product);
      },
      set(sizeValue) {
        const selectedOption = this.productSizeOptions.find(
          option => getSizeValue(getProductPackSize(option)) === sizeValue
        );
        this.setSelectedProductOption(selectedOption);
      }
    },
    cartItem() {
      return this.cartItems.find(item => getCartItemName(item) === this.productName);
    },
    usageDisplayValue() {
      const productName = getProductPackName(this.product);
      const usage = getProductPackUsage(this.product);
      const size = getProductPackSize(this.product);
      const monthQuantity = TreatmentDurationConfig.getDuration({
        productName,
        usage,
        size
      });
      const usageText = PRODUCT_USAGE[this.productName][usage] || PRODUCT_USAGE[this.productName];
      const key = this.isSerum
        ? 'productSubpage.usageAndDurationSerum'
        : 'productSubpage.usageAndDurationComplementary';

      return this.$t(key, {
        usage: this.$t(usageText),
        duration: monthQuantity
      });
    },
    isUSA() {
      return this.deliveryCountry.toUpperCase() === COUNTRY_ISO.UNITED_STATES;
    }
  },
  async created() {
    const initialSelectedOption = this.getInitialProductOption();

    this.setSelectedProductOption(initialSelectedOption);
    await this.setProductVideo();

    if (this.isEyeContourGeneric) {
      // init selected generic formula with the one from cart
      if (this.cartItem) {
        const [formulaId] = getCartItemFormulaIds(this.cartItem);
        this.setSelectedEyeSerumGenericFormulaId(formulaId);
      } else {
        const defaultFormulaId = this.eyeSerumGenericFormulaIds.find(id =>
          id.startsWith('WRINKLES')
        );
        this.setSelectedEyeSerumGenericFormulaId(defaultFormulaId);
      }
    }
  },
  methods: {
    ...mapMutations({
      setIsCartOpen: types.mutations.SET_IS_CART_OPEN,
      setSelectedEyeSerumGenericFormulaId: types.mutations.SET_SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    ...mapActions({
      putProductPackInCartOrOpenCart: types.actions.PUT_PRODUCT_PACK_IN_CART_OR_OPEN_CART
    }),
    getIsProductOptionDisabled(productOption) {
      return !isProductPackInStock(productOption) || !isProductPackAvailable(productOption);
    },
    getInitialProductOption() {
      const defaultOption = getProductOptionsDefaultOption(
        this.allProductOptions[this.productName]
      );

      return this.cartItem ? getCartItemProductPack(this.cartItem) : defaultOption;
    },
    setSelectedProductOption(productPack) {
      this.selectedProductOption = productPack;
    },
    async showDoYouWantToChangeSelectedOptionInCartPopup() {
      if (this.isEyeSerumGeneric) {
        const nextFormulaId = getProductPackGenericEyeSerumFormulaId(this.product);
        const [previousFormulaId] = getCartItemFormulaIds(this.cartItem);

        this.switchFormulaWarningModalConfig = {
          previousFormulaName: EYE_SERUM_GENERIC_FORMULA_LABELS[previousFormulaId],
          nextFormulaName: EYE_SERUM_GENERIC_FORMULA_LABELS[nextFormulaId]
        };

        return this.$refs.switchFormulaWarningPopup.open();
      }

      const previousSize = getCartItemSize(this.cartItem);
      const nextSize = getProductPackSize(this.product);

      this.changeVolumeWarningConfig = {
        previousSize,
        nextSize
      };

      return this.$refs.changeVolumeWarningPopup.open();
    },
    setProductOptionFromCart() {
      const productPack = this.getProductFromCart(this.productName);

      this.selectedProductOption = productPack;
    },
    async onAddProductToCart() {
      if (this.isOtherProductOptionInCart) {
        const yes = await this.showDoYouWantToChangeSelectedOptionInCartPopup();

        if (!yes) {
          return;
        }
      }

      if (!this.isProductInCart) {
        await this.putProductPackInCartOrOpenCart(this.product);

        return;
      }

      this.setIsCartOpen(true);
    },
    formatProductSize(product) {
      const size = getProductPackSize(product);
      const value = getSizeValue(size);
      const units = getSizeUnits(size);
      const unitsTranslated = this.$t(SIZE_MEASUREMENT_UNIT_KEY[units]);

      return formatProductSize({ value, units: unitsTranslated });
    },
    getProductSizeValue(productPack) {
      return getSizeValue(getProductPackSize(productPack));
    },
    async setProductVideo() {
      // TODO: refactor video files (remove unused and rename what's left)
      //  add missing videos
      try {
        this.productVideo = (
          await import(`@/assets/videos/media-galery/large/${this.productName}/2.mp4`)
        ).default;
      } catch (error) {
        log.warn(`Could not load the video ${this.productName}/2.mp4}`);

        this.productVideo = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.product-details-page {
  --horizontal-padding: var(--ws-grid-m, 16px);
}

.sub-navigation {
  --u-icon-size: 20px;
  padding: 24px var(--horizontal-padding);
  display: flex;
  align-items: center;
  color: var(--u-color-grey-900);

  &__nav-item:not(:last-child) {
    margin-inline-end: 8px;
  }

  &__nav-item {
    text-decoration: none;

    &--grey {
      color: var(--u-color-grey-500);
    }
  }
}

.nav-link {
  display: flex;
  align-items: center;

  &__icon {
    margin-inline-end: 24px;

    &:lang(ar) {
      transform: scaleX(-1);
    }
  }
}

.gallery-section {
  padding: 0 var(--horizontal-padding);

  &__gallery {
    margin-bottom: 32px;
    margin-inline-end: calc(-1 * var(--horizontal-padding));
  }

  &__description {
    max-width: 508px;
    margin-inline-start: auto;
    margin-inline-end: auto;
  }
}

.gallery-section-description {
  &__common {
    margin-top: 24px;
    margin-bottom: 32px;
  }
}

.common-description {
  &__paypal-option {
    margin-top: 15px;
  }

  &__add-to-cart-button {
    margin-top: 24px;
    margin-bottom: 67px;
    width: 100%;
  }

  &__usa-tax-hint {
    margin-top: 8px;
    color: var(--u-color-grey-500);
  }
}

.options {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  &__size-select-button {
    margin-inline-end: 16px;
  }

  &__size-select-button:nth-child(2) {
    margin-inline-start: 24px;
  }

  &__size-option {
    margin-inline-start: 16px;
    padding-inline-start: 16px;
    border-inline-start: 1px solid var(--u-color-grey-300);
  }
}

.video-section {
  &__video {
    width: 100%;
    height: 430px;
    object-fit: cover;
    object-position: center center;
  }
}

.catalog-section {
  padding: 52px var(--horizontal-padding);

  &__title {
    margin-bottom: 24px;
  }
}

.footer-section {
  padding: 32px var(--horizontal-padding);
  color: var(--u-color-grey-500);
  font-family: var(--u-font-family-Helvetica);
  font-size: 14px;
  line-height: 23px;
  border-top: 1px solid var(--u-color-grey-200);

  &__copyright {
    margin-bottom: 8px;
  }

  &__links {
    font-family: var(--u-font-family-Gilroy);
    line-height: 24px;
    letter-spacing: 0.75px;
  }
}

.copyright,
.footer-links {
  display: flex;
  align-items: center;
}

.footer-links {
  flex-wrap: wrap;

  &__link {
    color: inherit;
    text-decoration: none;
  }

  &__bullet {
    margin: 0 8px;
  }
}

@media (min-width: 960px) {
  .sub-navigation {
    padding: 32px var(--horizontal-padding);

    &__nav-item:not(:last-child) {
      margin-inline-end: 16px;
    }
  }

  .gallery-section {
    &__description {
      max-width: unset;
    }
  }

  .common-description {
    &__add-to-cart-button {
      min-width: 290px;
      min-height: 64px;
    }
  }

  .video-section {
    &__video {
      height: 522px;
    }
  }

  .footer-section {
    display: flex;
    justify-content: space-between;

    &__copyright {
      margin-bottom: 0;
    }
  }

  .footer-links {
    &__link {
      color: inherit;
      text-decoration: none;
    }

    &__bullet {
      margin: 0 16px;
    }
  }

  .catalog-section {
    &__title {
      margin-bottom: 46px;
    }
  }
}

@media (min-width: 1200px) {
  .gallery-section {
    display: flex;
    align-items: flex-start;
    position: relative;

    &__gallery,
    &__description {
      position: sticky;
      top: 94px;
    }

    &__gallery {
      margin-inline-end: var(--ws-grid-g);
    }

    &__description {
      margin: 0 auto;
      max-width: 436px;
    }
  }

  .common-description {
    &__add-to-cart-button {
      width: unset;
    }
  }

  .gallery-section-description {
    &__common {
      margin-bottom: 72px;
    }
  }
}

@media (min-width: 1920px) {
  .video-section {
    &__video {
      height: 740px;
    }
  }
}
</style>
